import React, { useState } from 'react';
import { ModalProps } from '@modals/types';
import { AddUserSpaceMemberMethod } from './enums';
import { Alignment, Button, Card, Divider, Elevation, Icon, Intent, RadioCard, RadioGroup } from '@blueprintjs/core';
import Flex from '@app/components/Flex';
import Heading from '@components/Heading';
import Overlay from '@components/Overlay';
import DevText from '@components/Text';
import { useForm, useTextFormField } from '@app/hooks';
import { getStringEmailValidator, getStringRequiredValidator } from '@app/hooks/validation/functions';
import InputFormField from '@app/components/InputFormField';
import { addParticipant, fetchClientTeamMembers, inviteUserToUserSpace } from '../../store/effects';

export interface AddUserSpaceMemberModalProps {
    userSpaceId: ID;
    onAddSuccess?: () => void;
}

type Props = ModalProps<AddUserSpaceMemberModalProps>;

const emailValidators = [getStringRequiredValidator(), getStringEmailValidator()];
const userIdValidators = [getStringRequiredValidator()];

const AddUserSpaceMemberModal: React.FC<Props> = ({ closeModal, data }) => {
    const [isCompleted, setIsCompleted] = useState(false);

    // Selected method
    const [method, setMethod] = useState<AddUserSpaceMemberMethod>(AddUserSpaceMemberMethod.Invite);

    const email = useTextFormField({
        id: 'email',
        initialValue: '',
        validators: emailValidators,
    });

    const userId = useTextFormField({
        id: 'id',
        initialValue: '',
        validators: userIdValidators,
    });

    const getFormFields = () => {
        switch (method) {
            case AddUserSpaceMemberMethod.Invite:
                return [email];
            case AddUserSpaceMemberMethod.UserID:
                return [userId];
        }
    };

    const form = useForm({
        fields: getFormFields(),
        apiCall: async () => {
            try {
                switch (method) {
                    case AddUserSpaceMemberMethod.Invite:
                        await inviteUserToUserSpace({ email: email.value, user_space_id: data!.userSpaceId });
                        break;
                    case AddUserSpaceMemberMethod.UserID:
                        await addParticipant({
                            userSpaceId: data!.userSpaceId,
                            input: {
                                participant_user_id: userId.value,
                            },
                        });
                        await fetchClientTeamMembers(data!.userSpaceId);
                        break;
                }

                return { response: true };
            } catch (e) {
                throw e;
            }
        },
        onSuccess: () => {
            setIsCompleted(true);
            data?.onAddSuccess?.();
        },
    });

    const handleModalClose = () => {
        if (form.isSubmitting) {
            return;
        }

        closeModal?.();
    };

    if (!data) {
        handleModalClose();
        return null;
    }

    const renderProcessComplete = () => {
        if (!isCompleted) {
            return;
        }

        let heading = '';
        switch (method) {
            case AddUserSpaceMemberMethod.Invite:
                heading = 'Invite sent';
                break;
            case AddUserSpaceMemberMethod.UserID:
                heading = 'User added to a team';
                break;
            default:
                heading = 'Success';
        }

        let caption = '';
        switch (method) {
            case AddUserSpaceMemberMethod.Invite:
                caption = 'Invite has been sent to the provided email';
                break;
            case AddUserSpaceMemberMethod.UserID:
                caption = 'User successfully added to a team';
                break;
            default:
                caption = 'Operation successful';
        }

        return (
            <div>
                <Flex align="center" className="mb-2">
                    <Icon icon="tick" size={35} intent={Intent.SUCCESS} className="mr-1" />
                    <Heading type="h5">{heading}</Heading>
                </Flex>
                <DevText muted className="mb-2">
                    {caption}
                </DevText>
                <DevText muted className="mb-2">
                    You can now close this window
                </DevText>
            </div>
        );
    };

    const renderMethodSelection = () => {
        return (
            <div className="mb-3">
                <Heading type="h5" className="mb-2">
                    How to add user?
                </Heading>

                <RadioGroup
                    disabled={form.isSubmitting}
                    selectedValue={method}
                    onChange={(e) => setMethod(e.currentTarget.value as AddUserSpaceMemberMethod)}
                    className="control-card-group-row"
                >
                    <RadioCard
                        elevation={Elevation.TWO}
                        alignIndicator={Alignment.RIGHT}
                        label="Invite"
                        value={AddUserSpaceMemberMethod.Invite}
                    >
                        <DevText className="mb-1">By invite</DevText>
                        <DevText muted>Send invite to the provided email</DevText>
                    </RadioCard>

                    <RadioCard
                        elevation={Elevation.TWO}
                        alignIndicator={Alignment.RIGHT}
                        label="By User ID"
                        value={AddUserSpaceMemberMethod.UserID}
                    >
                        <DevText className="mb-1">By user ID</DevText>
                        <DevText muted>Use this if you know what you are doing</DevText>
                    </RadioCard>
                </RadioGroup>
            </div>
        );
    };

    const renderInviteForm = () => {
        return (
            <form onSubmit={form.handleFormSubmit}>
                <InputFormField
                    field={email}
                    formGroupProps={{ label: 'Email' }}
                    inputProps={{ placeholder: 'Enter email' }}
                />

                <Flex align="center" justify="space-between">
                    <DevText muted>Proceed with user invitation?</DevText>
                    <div>
                        <Button minimal onClick={closeModal} className="mr-1" disabled={form.isSubmitting}>
                            No, cancel
                        </Button>
                        <Button
                            intent={Intent.PRIMARY}
                            loading={form.isSubmitting}
                            disabled={form.hasFieldErrors}
                            type="submit"
                        >
                            Yes, invite
                        </Button>
                    </div>
                </Flex>
            </form>
        );
    };

    const renderUserIdForm = () => {
        return (
            <form onSubmit={form.handleFormSubmit}>
                <InputFormField
                    field={userId}
                    formGroupProps={{ label: 'User ID' }}
                    inputProps={{ placeholder: 'Enter user ID' }}
                />

                <Flex align="center" justify="space-between">
                    <DevText muted>Proceed with adding user to the team?</DevText>
                    <div>
                        <Button minimal onClick={closeModal} className="mr-1" disabled={form.isSubmitting}>
                            No, cancel
                        </Button>
                        <Button
                            intent={Intent.PRIMARY}
                            loading={form.isSubmitting}
                            disabled={form.hasFieldErrors}
                            type="submit"
                        >
                            Yes, add
                        </Button>
                    </div>
                </Flex>
            </form>
        );
    };

    const renderForm = () => {
        switch (method) {
            case AddUserSpaceMemberMethod.Invite:
                return renderInviteForm();
            case AddUserSpaceMemberMethod.UserID:
                return renderUserIdForm();
        }
    };

    const renderContent = () => {
        if (isCompleted) {
            return renderProcessComplete();
        }

        return (
            <div>
                {renderMethodSelection()}
                {renderForm()}
            </div>
        );
    };

    return (
        <Overlay isOpen onClose={closeModal}>
            <Card style={{ width: '558px' }}>
                <Flex className="mb-2" align="center" justify="space-between">
                    <Heading type="h4">Add user to a team</Heading>
                    <Button minimal disabled={form.isSubmitting} icon="cross" onClick={closeModal} />
                </Flex>

                <Divider className="mb-2" />

                {renderContent()}
            </Card>
        </Overlay>
    );
};

export default AddUserSpaceMemberModal;
